import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiClient, ApiClientError } from './client'
import { DEFAULT_CLIENT_CONFIG, type ClientConfig } from '../types/client-config'

/**
 * useClientConfig — 拉 `GET {g}/config/client` 的纯展示参数。
 *
 * 行为：
 *  - 启动期 1 次，跟随 server `Cache-Control: max-age=300` 在 react-query
 *    侧做 5 分钟 staleTime 客户端缓存（与 server 缓存对齐，避免每次刷页都打）。
 *  - 失败时（5xx / 网络断 / endpoint 未实装）fallback 到 `DEFAULT_CLIENT_CONFIG`
 *    保证 UI 不卡在 loading 态。**不抛错** — caller 永远拿到一个可用的 config。
 *  - 缓存 key 仅 `['config', 'client']`，4 SPA 共享同一份。
 *
 * 与 server `Cache-Control: max-age=300` 配合：浏览器层 HTTP cache + react-query
 * staleTime 双层保护，5 分钟内基本不会真打 server。
 *
 * Spec: `docs/api-list/user/profile-settings.md` §3 / Task F 背景说明。
 */
export function useClientConfig(): UseQueryResult<ClientConfig, ApiClientError> & {
  config: ClientConfig
} {
  const q = useQuery<ClientConfig, ApiClientError>({
    queryKey: ['config', 'client'] as const,
    queryFn: async () => {
      try {
        const cfg = await apiClient.get<ClientConfig>('/gapi/public/config/client')
        // 防御：server 返空对象 / 缺字段时回落到默认
        return mergeWithDefaults(cfg)
      } catch (err) {
        // endpoint 未实装阶段直接吞掉，让 UI 走默认值 — 这是有意的降级。
        if (err instanceof ApiClientError && err.status >= 500) {
          return DEFAULT_CLIENT_CONFIG
        }
        if (err instanceof ApiClientError && err.code === 'NETWORK_ERROR') {
          return DEFAULT_CLIENT_CONFIG
        }
        throw err
      }
    },
    staleTime: 5 * 60 * 1000, // 5 min，对齐 server Cache-Control: max-age=300
    gcTime: 30 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    ...q,
    /** 始终非空：失败 / loading 时返回 DEFAULT_CLIENT_CONFIG */
    config: q.data ?? DEFAULT_CLIENT_CONFIG,
  }
}

/**
 * mergeWithDefaults — server 返回部分字段缺失时，逐字段回落到默认。
 *
 * 这样 server 可以增量增加新字段（不用先更新 client），同时 client 升级了
 * type 但 server 还没返新字段时也不会拿到 undefined。
 *
 * 不变量：返回的对象与 `DEFAULT_CLIENT_CONFIG` 同 shape；不深合并 ReadonlyArray。
 */
function mergeWithDefaults(cfg: Partial<ClientConfig> | null | undefined): ClientConfig {
  if (!cfg || typeof cfg !== 'object') return DEFAULT_CLIENT_CONFIG

  return {
    pwa_client: {
      ...DEFAULT_CLIENT_CONFIG.pwa_client,
      ...(cfg.pwa_client ?? {}),
    },
    messaging: {
      sla: {
        ...DEFAULT_CLIENT_CONFIG.messaging.sla,
        ...(cfg.messaging?.sla ?? {}),
      },
    },
    audit_retention: {
      display: {
        ...DEFAULT_CLIENT_CONFIG.audit_retention.display,
        ...(cfg.audit_retention?.display ?? {}),
      },
    },
    pack: {
      expires_at_options:
        cfg.pack?.expires_at_options ?? DEFAULT_CLIENT_CONFIG.pack.expires_at_options,
    },
    ...(cfg.locales !== undefined
      ? { locales: cfg.locales }
      : DEFAULT_CLIENT_CONFIG.locales !== undefined
        ? { locales: DEFAULT_CLIENT_CONFIG.locales }
        : {}),
  }
}

// 仅测试可见
export const _internal = { mergeWithDefaults }
